/*
  /terms — Sentio Mind terms of service.

  Same block tuples as privacyContent.js. Values that arrived as placeholders in
  the source copy are collected in the constants below so legal can change a
  notice period, a refund window or the liability cap in one place rather than
  hunting through 26 sections.
*/

export const TERMS_EFFECTIVE = "4 September 2026";
export const TERMS_UPDATED = "4 September 2026";

const SUPPORT_EMAIL = "support@sentiomind.in";
const REGISTERED_ADDRESS = "1111, Y Block, Kidwai Nagar,\nKanpur, Uttar Pradesh – 208011";
const JURISDICTION_CITY = "Kanpur, Uttar Pradesh";
/* Booking notice, refund window, liability cap and the notice period for a
   change to these Terms. */
const CANCEL_NOTICE = "12 hours";
const REFUND_WINDOW_DAYS = 7;
const LIABILITY_CAP = "Rs 5,000";
const TERMS_CHANGE_NOTICE_DAYS = 15;

export const termsDoc = {
  slug: "terms",
  eyebrow: "Legal · Terms",
  title: "Terms of Service",
  titleAccent: "SGRG Technologies Private Limited — “Sentio Mind”",
  sub:
    "The agreement between you and SGRG Technologies Private Limited for your use of the Sentio Mind app, platform and services — including what Sentio Mind is, what it is not, and how expert consultations, tiers and payments work.",
  effective: TERMS_EFFECTIVE,
  updated: TERMS_UPDATED,

  intro: [
    "These Terms of Service govern your use of the Sentio Mind app, website, platform and related services, operated by SGRG Technologies Private Limited (CIN: U62090UP2025PTC240171).",
    "Please read them together with our Privacy Policy, which forms part of these Terms.",
  ],

  sections: [
    {
      id: "accepting",
      num: 1,
      title: "Accepting these Terms",
      blocks: [
        ["ul", [
          "By creating an account, tapping “I agree”, or using any part of SentioMind, you agree to these Terms.",
          "If you do not agree, please do not use the App.",
          "These Terms apply to every user — whether you use the free Standard tier or Premium.",
          "Separate terms may apply to specific services (for example, expert consultation terms or payment gateway terms). Where they conflict, the specific terms apply to that service.",
        ]],
      ],
    },
    {
      id: "who-can-use",
      num: 2,
      title: "Who can use SentioMind",
      blocks: [
        ["ul", [
          "You must be 18 years or older.",
          "You must be legally able to enter into a contract under the Indian Contract Act, 1872.",
          "You must provide accurate information about yourself and keep it up to date.",
          "One account per person. Accounts are personal and may not be shared, sold or transferred.",
        ]],
        ["p", "We may suspend or close accounts that we reasonably believe belong to a minor or contain false information."],
      ],
    },
    {
      id: "what-it-is",
      num: 3,
      title: "What SentioMind is — and what it is not",
      blocks: [
        ["h", "3.1 What it is"],
        ["ul", [
          "A wellbeing app that helps you track sleep, nutrition, mood, movement and stress.",
          "A service that turns your own logged data into observations, patterns, trends and suggestions.",
          "A platform that lets you book sessions with independent qualified professionals.",
        ]],
        ["h", "3.2 What it is not"],
        ["ul", [
          "Not a medical device, hospital, clinic, diagnostic laboratory or pharmacy.",
          "Not a substitute for professional medical advice, diagnosis or treatment.",
          "Not an emergency service and not monitored in real time by a human.",
          "Not a provider of prescriptions, lab tests or medical certificates.",
        ]],
        ["note", "Always consult a qualified doctor about any medical condition, symptom, medication or treatment. Never delay or disregard professional medical advice because of something you read in SentioMind."],
      ],
    },
    {
      id: "emergencies",
      num: 4,
      title: "Emergencies and crisis situations",
      blocks: [
        ["note", "If you are experiencing a medical emergency, call your local emergency number immediately. If you are having thoughts of harming yourself or others, contact a mental-health helpline, a doctor, or a trusted person right away."],
        ["ul", [
          "Where the App detects risk indicators, it may show supportive information and helpline details. This is informational support, not intervention, rescue or clinical care.",
          "SentioMind does not monitor your entries continuously and cannot guarantee that any risk will be detected.",
          "Do not rely on the App, chat, notifications or bookings to obtain urgent help.",
        ]],
      ],
    },
    {
      id: "account-security",
      num: 5,
      title: "Your account and security",
      blocks: [
        ["ul", [
          "Keep your login credentials and device secure. You are responsible for activity under your account.",
          `Tell us promptly at ${SUPPORT_EMAIL} if you suspect unauthorised access.`,
          "We may require re-authentication for sensitive actions such as changing payment details or deleting data.",
          "You may close your account at any time from Settings.",
        ]],
      ],
    },
    {
      id: "experts",
      num: 6,
      title: "Experts, consultations and bookings",
      blocks: [
        ["h", "6.1 The experts"],
        ["ul", [
          "Expert categories include nutritionists, counsellors, behavioural scientists, physicians, sleep coaches and movement coaches.",
          "Experts are independent professionals or independent contractors. They are not employees of SentioMind unless expressly stated.",
          "Experts hold their own qualifications, registrations and, where applicable, professional indemnity cover. We verify credentials at onboarding but we do not practise medicine or provide clinical services ourselves.",
          "Each expert advises only within their own scope of practice and applicable professional and regulatory standards.",
          "Any doctor-patient or client-professional relationship is between you and that expert, not between you and SentioMind.",
        ]],

        ["h", "6.2 Booking, cancelling and conduct"],
        ["ul", [
          "Free tier: one session per expert category per quarter. Premium: all categories, unlimited under a fair-use policy.",
          "Fair use means normal personal use. We may apply reasonable limits where usage is excessive, automated or commercial.",
          `Sessions must be cancelled or rescheduled by ${CANCEL_NOTICE} before the start time. Late cancellations or no-shows may count against your allowance.`,
          "Before a session we may share a short summary of your logs and scores with the expert. You can review and edit that summary before it is sent.",
          "Sessions may not be recorded, photographed or shared by either party without written consent from the other.",
          "Abusive, threatening, discriminatory or harassing behaviour towards an expert will result in immediate suspension.",
          "Do not approach experts outside the platform for services first arranged through SentioMind.",
        ]],

        ["h", "6.3 Advice you receive"],
        ["ul", [
          "Advice given by an expert is their professional opinion, based on the information you provide. Its accuracy depends on the accuracy of what you share.",
          "SentioMind is not responsible for the content, quality or outcome of any expert’s advice.",
          "Consultations delivered through the App are not appropriate for emergencies, acute conditions or anything requiring physical examination.",
        ]],
      ],
    },
    {
      id: "for-experts",
      num: 7,
      title: "If you are an expert on SentioMind",
      blocks: [
        ["p", "This section applies in addition to your professional services agreement with us."],
        ["ul", [
          "You confirm that your qualifications, registrations and licences are genuine, current and disclosed to us, and that you will notify us immediately if any lapses.",
          "You will practise only within your scope of practice and will refer users onward where a case falls outside it.",
          "You will follow the applicable professional code of ethics and confidentiality obligations at all times.",
          "You will escalate any risk of harm through the escalation pathway set out in the platform, without delay.",
          "You will not diagnose, prescribe or advise beyond what your registration permits.",
          "You will keep all user information confidential and use it only to deliver the session, in line with the Privacy Policy and applicable law.",
          "You will not solicit users off-platform, share user data with third parties, or use user data for research or marketing without documented consent.",
          "You are responsible for your own taxes, insurance and regulatory compliance.",
        ]],
        ["p", "We may remove any expert from the platform where there is a credible complaint, a safety concern or a breach of these Terms."],
      ],
    },
    {
      id: "tiers",
      num: 8,
      title: "Standard and Premium",
      blocks: [
        ["ul", [
          "Standard is the free tier. Premium is the advanced version of the same features plus access to all expert categories. Premium is not a separate product.",
          "Safety-critical features are identical on both tiers: crisis routing, medical-risk escalation, emergency information and the ability to reach a human are never gated or delayed by payment.",
          "Paying does not change the evidence threshold at which any recommendation appears.",
          "We may change what is included in each tier. Material reductions to a paid tier will be notified in advance and you may cancel.",
        ]],
      ],
    },
    {
      id: "two-doors",
      num: 9,
      title: "Two ways to reach Premium",
      blocks: [
        ["h", "9.1 Door A — Insight Exchange (no payment)"],
        ["ul", [
          "You complete a structured profile across the modules shown in the App.",
          "Premium is unlocked when you meet the stated completeness and quality conditions and give explicit exchange consent.",
          "You may be asked to re-confirm changed facts periodically. If you do not, you revert to Standard after a grace period and a prompt — never silently.",
          "You may withdraw exchange consent at any time. You then return to Standard and the exchange-only fields are deleted within the period stated in the Privacy Policy.",
        ]],
        ["h", "9.2 Door B — Rs 79 per month"],
        ["ul", [
          "Rs 79 per month, inclusive of applicable taxes, charged by UPI Autopay, card e-mandate or net-banking mandate.",
          "No profile completion is required and no extra data is collected as a condition of paying.",
        ]],
        ["note", "Both doors give identical Premium entitlement, features and expert access. Both options are always shown together with equal prominence. Neither is pre-selected."],
      ],
    },
    {
      id: "payments",
      num: 10,
      title: "Payments, autopay and billing",
      blocks: [
        ["ul", [
          "Payments are processed by our payment gateway partner. Your card or UPI credentials are never stored by SentioMind — we hold only a subscription reference.",
          "You authorise a recurring mandate for Rs 79 per month, valid until you cancel.",
          "The price is shown before any authentication step. You approve the mandate inside your own bank or UPI app.",
          "A pre-debit notice is sent 24 hours before every debit, as required for recurring mandates.",
          "An invoice is issued for each successful debit and stored in your account.",
          "If a debit fails, we retry on a set schedule, then give a 7-day grace period with full access, then revert you to Standard.",
          "Prices may change. Any change is notified in advance and requires fresh authorisation. Nothing is charged at a new price without your consent.",
        ]],
      ],
    },
    {
      id: "cancellation",
      num: 11,
      title: "Cancellation, refunds and downgrade",
      blocks: [
        ["ul", [
          "You can cancel in two taps from Settings, at any time, without contacting support.",
          "Cancellation stops future renewals immediately. Premium access continues until the end of the period you have already paid for.",
          "Cancellation is confirmed in writing within minutes.",
          "Payments already made are generally non-refundable, except where required by law or where we have made a billing error.",
          `To request a refund for a billing error, write to ${SUPPORT_EMAIL} within ${REFUND_WINDOW_DAYS} days with the invoice reference.`,
        ]],
        ["note", "Losing Premium never deletes your history. Advanced surfaces are hidden; your trackers, insights, baselines and booking history remain. If you re-qualify or re-subscribe, your data is exactly as you left it."],
      ],
    },
    {
      id: "your-data",
      num: 12,
      title: "Your data and privacy",
      blocks: [
        ["ul", [
          "How we collect, use, store and share your data is set out in the Privacy Policy, which forms part of these Terms.",
          "We process personal data in line with the Digital Personal Data Protection Act, 2023 and other applicable Indian law.",
          "You can export your data, correct it, withdraw consent, or delete your account from Settings.",
          "Health information is sensitive. We ask for your explicit consent before processing it and you may withdraw that consent at any time.",
          "Withdrawing consent may mean parts of the App stop working for you. We will tell you what will stop before you confirm.",
          "We do not sell your personal data.",
        ]],
      ],
    },
    {
      id: "automated-insights",
      num: 13,
      title: "Automated insights, scores and recommendations",
      blocks: [
        ["ul", [
          "SentioMind uses automated systems, statistical models and AI to generate insights, scores, predictions and suggestions from your data.",
          "Scores are indicators of need or pattern. They are not clinical scores and are never labelled with a medical condition.",
          "Predictions describe patterns in your own history. They are not forecasts about your health and may be wrong.",
          "Automated systems never make a final decision about your medical care.",
          "You can see why any suggestion appeared, dismiss it, mute a category, pause personalisation, or reset your baselines from Settings.",
        ]],
        ["note", "Silence is normal. If the evidence does not support a recommendation, the App will not show one."],
      ],
    },
    {
      id: "acceptable-use",
      num: 14,
      title: "Acceptable use",
      blocks: [
        ["p", "You agree not to:"],
        ["ul", [
          "Use the App for any unlawful, harmful, fraudulent or abusive purpose.",
          "Impersonate another person, or create an account on someone else’s behalf without authority.",
          "Enter false or misleading health information, or attempt to manipulate scores or unlock conditions.",
          "Copy, scrape, reverse-engineer, decompile or attempt to extract the source code, models or algorithms.",
          "Use bots, scripts or automated tools to access the App.",
          "Interfere with security, probe for vulnerabilities, or overload our systems.",
          "Resell, sublicense or commercially exploit the App or any content in it.",
          "Post or transmit anything unlawful, defamatory, obscene, hateful or infringing.",
          "Use the App or any expert advice to provide services to third parties.",
        ]],
      ],
    },
    {
      id: "ip",
      num: 15,
      title: "Content and intellectual property",
      blocks: [
        ["ul", [
          "SentioMind, its name, logo, design, content, models and software are owned by us or our licensors. All rights reserved.",
          "We grant you a limited, personal, non-exclusive, non-transferable, revocable licence to use the App for your own personal wellbeing.",
          "You keep ownership of the data and content you submit. You grant us a licence to process it in order to run the service, as described in the Privacy Policy.",
          "Anonymised and aggregated data may be used to improve the service and its models, in line with the Privacy Policy.",
          "Feedback and suggestions you send us may be used without obligation or payment to you.",
          "Educational content in the App is for general information and is not personalised medical advice.",
        ]],
      ],
    },
    {
      id: "third-party",
      num: 16,
      title: "Third-party services",
      blocks: [
        ["ul", [
          "The App relies on third parties, including payment gateways, cloud hosting, communication providers and analytics providers.",
          "Links or references to third-party content are provided for convenience. We do not endorse and are not responsible for third-party services.",
          "Your use of a third-party service is governed by that party’s own terms.",
        ]],
      ],
    },
    {
      id: "availability",
      num: 17,
      title: "Availability and changes to the service",
      blocks: [
        ["ul", [
          "We aim for a reliable service but we do not guarantee uninterrupted or error-free availability.",
          "We may suspend the service for maintenance, upgrades, security or legal reasons.",
          "We may add, change, restrict or discontinue features. Where a change materially reduces a paid feature, we will notify you in advance.",
          "We may discontinue the App entirely with reasonable notice, giving you the opportunity to export your data.",
        ]],
      ],
    },
    {
      id: "disclaimers",
      num: 18,
      title: "Disclaimers",
      blocks: [
        ["ul", [
          "The App is provided “as is” and “as available”, to the fullest extent permitted by law.",
          "We do not warrant that insights, scores or predictions are accurate, complete or suitable for your circumstances.",
          "We do not warrant any particular health, wellbeing or clinical outcome from using the App.",
          "We are not liable for the acts, omissions or advice of any independent expert.",
          "You use the App at your own risk and remain responsible for your own health decisions.",
        ]],
      ],
    },
    {
      id: "liability",
      num: 19,
      title: "Limitation of liability",
      blocks: [
        ["ul", [
          "To the fullest extent permitted by law, we are not liable for indirect, incidental, special, consequential or punitive damages, or for loss of profit, data, goodwill or opportunity.",
          `Our total aggregate liability for any claim relating to the App is limited to the higher of (a) the amount you paid us in the 12 months before the claim, or (b) ${LIABILITY_CAP}.`,
          "Nothing in these Terms limits liability that cannot be limited by law, including liability for fraud, or for death or personal injury caused by our negligence.",
        ]],
      ],
    },
    {
      id: "indemnity",
      num: 20,
      title: "Indemnity",
      blocks: [
        ["p", "You agree to indemnify and hold us harmless from claims, losses and reasonable legal costs arising from your breach of these Terms, your misuse of the App, or your violation of any law or third-party right."],
      ],
    },
    {
      id: "termination",
      num: 21,
      title: "Suspension and termination",
      blocks: [
        ["ul", [
          "You may stop using the App and delete your account at any time.",
          "We may suspend or terminate your access if you breach these Terms, if there is a security or safety concern, or if required by law.",
          "Where practical we will give notice and an opportunity to fix the problem first.",
          "On termination, your licence to use the App ends. Data is retained or deleted as described in the Privacy Policy.",
          "Sections on intellectual property, disclaimers, liability, indemnity and governing law survive termination.",
        ]],
      ],
    },
    {
      id: "governing-law",
      num: 22,
      title: "Governing law and disputes",
      blocks: [
        ["ul", [
          "These Terms are governed by the laws of India.",
          `Courts at ${JURISDICTION_CITY} have exclusive jurisdiction, subject to the steps below.`,
          `Please contact us first at ${SUPPORT_EMAIL} — most issues are resolved quickly.`,
          `If a dispute is not resolved within 30 days, it may be referred to arbitration by a sole arbitrator under the Arbitration and Conciliation Act, 1996. Seat and venue: ${JURISDICTION_CITY}. Language: English.`,
        ]],
      ],
    },
    {
      id: "grievance",
      num: 23,
      title: "Grievance redressal",
      blocks: [
        ["dl", [
          ["Grievance officer", "Grievance Officer, SGRG Technologies Private Limited"],
          ["Email", SUPPORT_EMAIL],
          ["Address", REGISTERED_ADDRESS],
        ]],
        ["p", "We acknowledge complaints within 24 hours and aim to resolve them within 15 days, as required under Indian law."],
        ["p", "Data protection queries, including consent withdrawal and erasure requests, can be sent to the same address."],
      ],
    },
    {
      id: "changes",
      num: 24,
      title: "Changes to these Terms",
      blocks: [
        ["ul", [
          "We may update these Terms from time to time.",
          `Material changes will be notified in the App and by email at least ${TERMS_CHANGE_NOTICE_DAYS} days before they take effect.`,
          "You will be asked to accept the updated Terms through the in-app pop-up.",
          "Continuing to use the App after the effective date means you accept the updated Terms. If you do not accept, you may close your account.",
        ]],
      ],
    },
    {
      id: "general",
      num: 25,
      title: "General",
      blocks: [
        ["ul", [
          "If any clause is found invalid, the rest of the Terms remain in force.",
          "Not enforcing a right immediately does not mean we waive it.",
          "These Terms, together with the Privacy Policy, are the entire agreement between you and us regarding the App.",
          "We may transfer our rights under these Terms as part of a merger, acquisition or restructuring, with notice to you.",
          "Notices to you may be sent in-app, by email or by SMS to the details on your account.",
        ]],
      ],
    },
    {
      id: "contact",
      num: 26,
      title: "Contact us",
      blocks: [
        ["dl", [
          ["Support", SUPPORT_EMAIL],
          ["Company", `SGRG Technologies Private Limited\n${REGISTERED_ADDRESS}`],
          ["Grievance officer", `Grievance Officer, SGRG Technologies Private Limited\n${SUPPORT_EMAIL}`],
          ["Website", "www.sentiomind.in"],
        ]],
        ["p", `Last Updated: ${TERMS_UPDATED}`],
      ],
    },
  ],

  crosslink: {
    text: "Want to know how your data is collected, used and protected?",
    label: "Read the Privacy Policy",
    to: "/privacy",
  },
};

export default termsDoc;
